import type { ApiClient } from "./client";
import type {
  CollectionMutationBody,
  CollectionMutationResult,
  CollectionSnapshot,
  CollectionHistory,
} from "./types";

export async function fetchCollection(
  client: ApiClient,
  options?: { signal?: AbortSignal },
): Promise<CollectionSnapshot> {
  return client.request<CollectionSnapshot>("/api/v1/collection", {
    signal: options?.signal,
  });
}

export async function fetchCollectionHistory(client: ApiClient, cursor?: string, limit = 25): Promise<CollectionHistory> {
  const query = new URLSearchParams({ limit: String(limit) });
  if (cursor) query.set("cursor", cursor);
  return client.request<CollectionHistory>(`/api/v1/collection/history?${query}`);
}

export async function undoCollectionActivity(client: ApiClient, activityId: string, expectedRevision: number): Promise<CollectionMutationResult> {
  return client.request<CollectionMutationResult>(`/api/v1/collection/history/${activityId}/undo`, { method: "POST", body: { expectedRevision } });
}

/**
 * Mutates owned quantity for a release.
 * Never send ownerId/accountId — server resolves ownership from the session.
 */
export async function putCollectionRelease(
  client: ApiClient,
  releaseId: string,
  body: CollectionMutationBody,
): Promise<CollectionMutationResult> {
  return client.request<CollectionMutationResult>(
    `/api/v1/collection/releases/${releaseId}`,
    {
      method: "PUT",
      body: {
        quantity: body.quantity,
        expectedRevision: body.expectedRevision,
      },
    },
  );
}

export type CollectionImportBody = {
  expectedRevision: number;
  mode: "replace";
  items: Array<{ releaseId: string; quantity: number }>;
};

/**
 * Atomic replace import. Server validates releases and revision in one transaction.
 */
export async function importCollectionReplace(
  client: ApiClient,
  body: CollectionImportBody,
): Promise<CollectionSnapshot> {
  return client.request<CollectionSnapshot>("/api/v1/collection/import", {
    method: "POST",
    body: {
      expectedRevision: body.expectedRevision,
      mode: "replace",
      items: body.items,
    },
  });
}
